import { useEffect, useRef } from 'react';
import { FullProject } from '../types';
import {
  planLegacyContactMigration,
  healCommProjectAssignments,
} from '../components/communication/personModel';
import { isSystemProject, areProjectIdsMatching } from '../utils/idNormalizer';
import { undoToastManager } from '../utils/undoToastManager';

interface UseLegacyContactHealingParams {
  isInitialLoadComplete: boolean;
  allProjects: FullProject[];
  setAllProjects: React.Dispatch<React.SetStateAction<FullProject[]>>;
  syncProjectToFirestore: (project: FullProject, isSilent?: boolean) => Promise<unknown> | void;
  viewMode: string;
  currentProjectId: string;
  migrateProjectToSortOrder: (projectId: string) => void;
}

export function useLegacyContactHealing({
  isInitialLoadComplete,
  allProjects,
  setAllProjects,
  syncProjectToFirestore,
  viewMode,
  currentProjectId,
  migrateProjectToSortOrder,
}: UseLegacyContactHealingParams) {
  useEffect(() => {
    if (viewMode === 'communication') {
      migrateProjectToSortOrder('comm_internal');
    } else if (currentProjectId && currentProjectId !== 'global') {
      migrateProjectToSortOrder(currentProjectId);
    }
  }, [viewMode, currentProjectId, migrateProjectToSortOrder]);

  const hasCheckedLegacyContactsRef = useRef(false);

  useEffect(() => {
    if (!isInitialLoadComplete || allProjects.length === 0) {
      return;
    }
    // Wait until at least one real user project is loaded alongside comm_internal / inbox_inforum
    const hasRealProjects = allProjects.some(p => !isSystemProject(p.id, p.name) && !p.archived);
    if (!hasRealProjects) return;

    let currentProjects = allProjects;
    const affectedProjectIds = new Set<string>();

    if (!hasCheckedLegacyContactsRef.current) {
      hasCheckedLegacyContactsRef.current = true;
      const plan = planLegacyContactMigration(currentProjects);
      if (plan.totalLegacyFound > 0) {
        try {
          const backupData = JSON.stringify({
            timestamp: Date.now(),
            count: plan.totalLegacyFound,
            projects: currentProjects,
          });
          sessionStorage.setItem(`gwantt_contact_migration_backup_${Date.now()}`, backupData);
        } catch (err) {
          console.warn('[AutoMigration] Failed to store backup in storage:', err);
        }

        currentProjects = plan.apply();
        plan.items.forEach(i => affectedProjectIds.add(i.projectId));

        undoToastManager.show({
          message: `✓ ${plan.totalLegacyFound} Kontakte erfolgreich in das Person ↔ Vorgang Modell überführt.`,
          durationMs: 7000,
          icon: 'status',
        });
      }
    }

    const healed = healCommProjectAssignments(currentProjects);
    if (healed.changedTasks.length > 0) {
      currentProjects = healed.updatedProjects;
      healed.changedTasks.forEach(t => {
        if (t.projectId) affectedProjectIds.add(t.projectId);
        (t.projectIds || []).forEach(pid => affectedProjectIds.add(pid));
      });
      (healed.affectedProjectIds || []).forEach(pid => affectedProjectIds.add(pid));
      affectedProjectIds.add('comm_internal');
    }

    if (affectedProjectIds.size > 0) {
      setAllProjects(currentProjects);
      currentProjects
        .filter(p => Array.from(affectedProjectIds).some(aid => areProjectIdsMatching(p.id, aid)))
        .forEach(p => {
          void syncProjectToFirestore(p, true);
        });
    }
  }, [isInitialLoadComplete, allProjects, setAllProjects, syncProjectToFirestore]);
}
